import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { StyledContainer } from "../../components/styles/styledContainer";

// game is open to all players
const isAllowed = () => true;

const SHIP_COLORS = ["#4caf7d", "#f0c674", "#5b8def", "#e05c6e"];
const ORD = ["1-е", "2-е", "3-е", "4-е"];
const KIND_LABEL = { winner: "побед.", exacta: "пара", trifecta: "тройка" };
const PLACE_COLOR = ["#f5c542", "#c8d0e0", "#cd7f4a", "#7a86b8"];
const BADGE_COLORS = ["", "#f5c542", "#c8d0e0", "#cd7f4a", "#7a86b8"];
function drawBadge(ctx, x, y, place, size) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, size, 0, Math.PI * 2);
  ctx.fillStyle = BADGE_COLORS[place] || "#7a86b8";
  ctx.shadowColor = ctx.fillStyle;
  ctx.shadowBlur = 8;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = "#0a0f26";
  ctx.font = "bold " + size * 1.15 + "px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(place), x, y + 0.5);
  ctx.textAlign = "start";
  ctx.textBaseline = "alphabetic";
  ctx.restore();
}

// ---------- canvas scene helpers (module scope, no allocation churn) ----------
const clampN = (v, a, b) => Math.max(a, Math.min(b, v));
const hexRgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
function makeStars(w, h, n) {
  const s = [];
  for (let i = 0; i < n; i++) {
    s.push({ x: Math.random() * w, y: Math.random() * h, z: 0.2 + Math.random() * 0.8 });
  }
  return s;
}
function drawBg(ctx, w, h) {
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, "#0a0f26");
  g.addColorStop(0.55, "#0b1030");
  g.addColorStop(1, "#090a1c");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  const rg = ctx.createRadialGradient(w * 0.78, h * 0.28, 0, w * 0.78, h * 0.28, w * 0.55);
  rg.addColorStop(0, "rgba(96,84,210,0.12)");
  rg.addColorStop(1, "rgba(96,84,210,0)");
  ctx.fillStyle = rg;
  ctx.fillRect(0, 0, w, h);
}
function drawStars(ctx, stars, w, h, dtMs, avgSp) {
  const spd = 26 + avgSp * 1700;
  for (const st of stars) {
    st.x -= spd * st.z * (dtMs / 1000);
    if (st.x < 0) {
      st.x += w;
      st.y = Math.random() * h;
    }
    const a = 0.18 + st.z * 0.6;
    const len = avgSp * 320 * st.z;
    if (len > 2) {
      ctx.strokeStyle = "rgba(170,190,255," + (a * 0.55).toFixed(2) + ")";
      ctx.lineWidth = st.z * 1.3;
      ctx.beginPath();
      ctx.moveTo(st.x, st.y);
      ctx.lineTo(st.x + len, st.y);
      ctx.stroke();
    } else {
      ctx.fillStyle = "rgba(205,214,255," + a.toFixed(2) + ")";
      ctx.fillRect(st.x, st.y, st.z * 1.4, st.z * 1.4);
    }
  }
}
function drawGate(ctx, x, h, ts) {
  const pulse = 0.6 + 0.4 * Math.sin(ts / 300);
  ctx.save();
  ctx.shadowColor = "rgba(130,160,255,0.9)";
  ctx.shadowBlur = 18;
  ctx.fillStyle = "rgba(150,180,255," + (0.4 * pulse + 0.35).toFixed(2) + ")";
  ctx.fillRect(x, 0, 4, h);
  ctx.restore();
  const sz = 9;
  for (let y = 0; y < h; y += sz) {
    ctx.fillStyle = Math.floor(y / sz) % 2 === 0 ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.12)";
    ctx.fillRect(x + 5, y, 4, sz);
  }
}
function drawFlame(ctx, cx, cy, size, sn, color, ts) {
  const len = size * (0.5 + sn * 2.4) * (0.82 + 0.18 * Math.sin(ts / 40));
  const tail = cx - size * 0.45;
  const [r, g, b] = hexRgb(color);
  const grd = ctx.createLinearGradient(tail, cy, tail - len, cy);
  grd.addColorStop(0, "rgba(255,255,255,0.95)");
  grd.addColorStop(0.3, "rgba(" + r + "," + g + "," + b + ",0.85)");
  grd.addColorStop(1, "rgba(" + r + "," + g + "," + b + ",0)");
  ctx.fillStyle = grd;
  ctx.beginPath();
  ctx.moveTo(tail, cy - size * 0.28);
  ctx.lineTo(tail - len, cy);
  ctx.lineTo(tail, cy + size * 0.28);
  ctx.closePath();
  ctx.fill();
}
function drawCraft(ctx, cx, cy, size, color, tilt, leader) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(tilt);
  const [r, g, b] = hexRgb(color);
  ctx.shadowColor = color;
  ctx.shadowBlur = leader ? 22 : 13;
  const grd = ctx.createLinearGradient(-size * 0.6, 0, size, 0);
  grd.addColorStop(0, "rgba(" + r + "," + g + "," + b + ",0.5)");
  grd.addColorStop(1, "#ffffff");
  ctx.fillStyle = grd;
  ctx.beginPath();
  ctx.moveTo(size, 0);
  ctx.lineTo(-size * 0.55, -size * 0.5);
  ctx.lineTo(-size * 0.28, 0);
  ctx.lineTo(-size * 0.55, size * 0.5);
  ctx.closePath();
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.beginPath();
  ctx.arc(size * 0.12, 0, size * 0.15, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
function drawBoostFlare(ctx, x, y, size, boost, ts) {
  const r = size * (0.6 + boost * 1.5) * (0.85 + 0.15 * Math.sin(ts / 22));
  ctx.save();
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, "rgba(255,255,255," + (0.8 * boost).toFixed(2) + ")");
  g.addColorStop(0.35, "rgba(255,214,120," + (0.6 * boost).toFixed(2) + ")");
  g.addColorStop(0.7, "rgba(255,120,70," + (0.35 * boost).toFixed(2) + ")");
  g.addColorStop(1, "rgba(255,90,60,0)");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
function drawShock(ctx, x, y, size, brake, ts) {
  ctx.save();
  const r = size * (0.9 + brake * 0.9);
  const flick = 0.85 + 0.15 * Math.sin(ts / 30);
  ctx.strokeStyle = "rgba(200,220,255,0.9)";
  ctx.globalAlpha = (0.15 + brake * 0.4) * flick;
  ctx.lineWidth = 1.5 + brake * 2;
  ctx.beginPath();
  ctx.arc(x, y, r, -Math.PI * 0.42, Math.PI * 0.42);
  ctx.stroke();
  ctx.globalAlpha *= 0.55;
  ctx.beginPath();
  ctx.arc(x - r * 0.32, y, r * 0.68, -Math.PI * 0.4, Math.PI * 0.4);
  ctx.stroke();
  ctx.restore();
}
function spawnExhaust(parts, x, y, sn, color) {
  if (sn < 0.1) return;
  const count = sn > 1.2 ? 3 : sn > 0.6 ? 2 : 1;
  for (let i = 0; i < count; i++) {
    parts.push({
      x,
      y: y + (Math.random() - 0.5) * 6,
      vx: -(38 + sn * 170) - Math.random() * 40,
      vy: (Math.random() - 0.5) * 28,
      life: 1,
      max: 0.4 + Math.random() * 0.4,
      color,
    });
  }
  if (parts.length > 340) parts.splice(0, parts.length - 340);
}
function drawParticles(ctx, parts, dtMs) {
  const dts = dtMs / 1000;
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i];
    p.life -= dts / p.max;
    if (p.life <= 0) {
      parts.splice(i, 1);
      continue;
    }
    p.x += p.vx * dts;
    p.y += p.vy * dts;
    const [r, g, b] = hexRgb(p.color);
    ctx.fillStyle = "rgba(" + r + "," + g + "," + b + "," + (p.life * 0.55).toFixed(2) + ")";
    const rad = 1 + p.life * 2.4;
    ctx.beginPath();
    ctx.arc(p.x, p.y, rad, 0, Math.PI * 2);
    ctx.fill();
  }
}

const MODES = [
  { key: "winner", label: "На победу", arity: 1, hint: "Выберите победителя" },
  { key: "exacta", label: "Пара 1→2 · x2", arity: 2, hint: "Выберите 1-е и 2-е место по порядку" },
  { key: "trifecta", label: "Тройка 1→2→3 · x3", arity: 3, hint: "Выберите 1-е, 2-е и 3-е (4-е определится само)" },
];
const modeArity = (m) => MODES.find((x) => x.key === m).arity;

const RACE_BASE = "/race";
const wsUrl = () =>
  (window.location.protocol === "https:" ? "wss://" : "ws://") +
  window.location.host + RACE_BASE + "/ws";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token
    ? { "Content-Type": "application/json", Authorization: "Bearer " + token }
    : { "Content-Type": "application/json" };
};

const short = (s) => (s || "").replace("USDT", "");

// isolated so the 100ms countdown re-renders only this node, not the whole page
const PhaseTimer = React.memo(({ phase, roundId, conn, attempt, endsAtRef }) => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setLeft(Math.max(0, (endsAtRef.current - Date.now()) / 1000)),
      100
    );
    return () => clearInterval(id);
  }, [endsAtRef]);
  return (
    <div className="phase">
      {phase ? phase.toUpperCase() + " — " + left.toFixed(1) + "s" : "…"}
      {roundId ? <span className="rid"> · раунд #{roundId}</span> : null}
      <span className={"conn " + conn}>
        {conn === "online"
          ? "● на связи"
          : conn === "connecting"
          ? "○ подключение…"
          : "● нет связи · переподключение " + attempt}
      </span>
    </div>
  );
});

const RacePage = () => {
  const [phase, setPhase] = useState("");
  const [roundId, setRoundId] = useState(0);
  const [pairs, setPairs] = useState([]);
  const [ships, setShips] = useState([]);
  const [markets, setMarkets] = useState(null);
  const [history, setHistory] = useState([]);
  const [amount, setAmount] = useState(1);
  const [currency, setCurrency] = useState("USDT");
  const [balance, setBalance] = useState(null);
  const [myBets, setMyBets] = useState([]);
  const [msg, setMsg] = useState("");
  const [feed, setFeed] = useState([]); // live bets of all players
  const [roundBets, setRoundBets] = useState([]); // bets on the current round
  const [conn, setConn] = useState("connecting"); // connecting | online | offline
  const [attempt, setAttempt] = useState(0);
  const [dirs, setDirs] = useState([[], [], [], []]); // last 10 tick directions per ship
  const [series, setSeries] = useState([[], [], [], []]); // cum_pct per tick per ship (chart)
  const [chartMode, setChartMode] = useState(false);
  const [tooSmall, setTooSmall] = useState(
    typeof window !== "undefined" && window.innerWidth < 600
  );
  const [visOrder, setVisOrder] = useState([]); // finish order by VISUAL arrival (short names)
  const arrivedRef = useRef([false, false, false, false]);
  const lastNRef = useRef(0); // last applied tick marker (dedup / mid-join sync)

  // static betting slip — persists across rounds, does NOT reset after a game
  const [mode, setMode] = useState("winner");
  const [picks, setPicks] = useState([0]); // winner mode → first card preselected

  const endsAtRef = useRef(0);
  const roundRef = useRef(0);
  const bettingOpenRef = useRef(false);

  // ---- smooth animation: rAF + Catmull-Rom over a fixed-cadence sample grid ----
  // samples are placed on a uniform logical timeline (not arrival time) so network
  // jitter can't warp the rendered speed; cubic interpolation keeps velocity
  // continuous across ticks, so speed changes don't produce visible kinks.
  const canvasRef = useRef(null);
  const sizeRef = useRef({ w: 800, h: 260, dpr: 1 });
  const starsRef = useRef([]);
  const particlesRef = useRef([]);
  const visSpeedRef = useRef([0, 0, 0, 0]);
  const prevVisRef = useRef([0, 0, 0, 0]);
  const accelSmoothRef = useRef([0, 0, 0, 0]);
  const tiltRef = useRef([0, 0, 0, 0]);
  const prevSpeedRef = useRef([]); // server speed last tick → acceleration in the table
  const shipsDataRef = useRef([]); // latest ship labels for the canvas

  const buffers = useRef([[], [], [], []]); // per-slot samples {t, pos}
  const headTRef = useRef(0); // logical time of the newest sample
  const clockRef = useRef(0); // render clock, advances in real time
  const lastFrameRef = useRef(0);
  const lastPosRef = useRef([0, 0, 0, 0]); // monotonic guard (never move back)
  const TICK_MS = 2000; // engine samples every 2s
  const RENDER_DELAY = 2200; // ~1 tick behind → always have the next sample to interp
  const LEFT_PAD = 54;
  const RIGHT_PAD = 66;

  const pushBatch = (posArr) => {
    let ht = headTRef.current + TICK_MS;
    const floor = clockRef.current + RENDER_DELAY;
    if (ht < floor) ht = floor; // resync if we drifted behind
    headTRef.current = ht;
    for (let i = 0; i < 4; i++) {
      const b = buffers.current[i];
      b.push({ t: ht, pos: posArr[i] || 0 });
      if (b.length > 12) b.shift();
    }
  };
  const resetBuffers = (posArr) => {
    const t0 = clockRef.current + RENDER_DELAY;
    headTRef.current = t0;
    buffers.current = posArr.map((p) => [
      { t: t0 - TICK_MS, pos: p },
      { t: t0, pos: p },
    ]);
    lastPosRef.current = posArr.slice();
  };

  const resizeCanvas = () => {
    const cv = canvasRef.current;
    if (!cv || !cv.parentElement) return;
    // never exceed the viewport → finish line always stays on screen
    const w = Math.max(
      280,
      Math.min(cv.parentElement.clientWidth || 800, window.innerWidth - 20)
    );
    const h = Math.round(clampN(w * 0.42, 200, 340));
    const dpr = window.devicePixelRatio || 1;
    cv.width = Math.round(w * dpr);
    cv.height = Math.round(h * dpr);
    cv.style.width = w + "px";
    cv.style.height = h + "px";
    sizeRef.current = { w, h, dpr };
    starsRef.current = makeStars(w, h, 150);
  };

  // Catmull-Rom position at logical time rt within buffer b
  const sample = (b, rt) => {
    if (!b.length) return 0;
    if (rt <= b[0].t) return b[0].pos;
    if (rt >= b[b.length - 1].t) return b[b.length - 1].pos;
    let k = 0;
    for (; k < b.length - 1; k++) if (b[k].t <= rt && b[k + 1].t >= rt) break;
    const p1 = b[k].pos;
    const p2 = b[k + 1].pos;
    const p0 = k > 0 ? b[k - 1].pos : p1;
    const p3 = k + 2 < b.length ? b[k + 2].pos : p2;
    const f = (rt - b[k].t) / Math.max(1, b[k + 1].t - b[k].t);
    const f2 = f * f;
    const f3 = f2 * f;
    return 0.5 * (2 * p1 +
      (-p0 + p2) * f +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * f2 +
      (-p0 + 3 * p1 - 3 * p2 + p3) * f3);
  };

  useEffect(() => {
    const onResize = () => setTooSmall(window.innerWidth < 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    resizeCanvas();
    let raf;
    const loop = (ts) => {
      if (!lastFrameRef.current) lastFrameRef.current = ts;
      const dt = Math.min(80, ts - lastFrameRef.current);
      lastFrameRef.current = ts;
      clockRef.current += dt;
      const rt = clockRef.current;
      const cv = canvasRef.current;
      if (!cv) {
        raf = requestAnimationFrame(loop);
        return;
      }
      const ctx = cv.getContext("2d");
      const { w, h, dpr } = sizeRef.current;
      const trackW = w - LEFT_PAD - RIGHT_PAD;
      const dts = Math.max(0.001, dt / 1000);

      // interpolate positions and derive smoothed visual speed / acceleration
      const pos = [0, 0, 0, 0];
      const sp = [0, 0, 0, 0];
      let bestPos = -1;
      let leader = 0;
      for (let i = 0; i < 4; i++) {
        let p = sample(buffers.current[i], rt);
        if (p < lastPosRef.current[i]) p = lastPosRef.current[i];
        if (p > 1) p = 1;
        const raw = Math.max(0, (p - lastPosRef.current[i]) / dts);
        lastPosRef.current[i] = p;
        const a = 1 - Math.exp(-dts / 0.12);
        prevVisRef.current[i] = visSpeedRef.current[i];
        visSpeedRef.current[i] += (raw - visSpeedRef.current[i]) * a;
        pos[i] = p;
        sp[i] = visSpeedRef.current[i];
        if (p > bestPos) {
          bestPos = p;
          leader = i;
        }
      }
      const avgSp = (sp[0] + sp[1] + sp[2] + sp[3]) / 4;

      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, w, h);
      drawBg(ctx, w, h);
      drawStars(ctx, starsRef.current, w, h, dt, avgSp);
      drawGate(ctx, LEFT_PAD + trackW, h, ts);

      const laneH = h / 4;
      const shipSize = clampN(laneH * 0.32, 12, 26);
      const data = shipsDataRef.current;
      const easeA = 1 - Math.exp(-dts / 0.18);
      const easeT = 1 - Math.exp(-dts / 0.16);
      for (let i = 0; i < 4; i++) {
        const cy = laneH * i + laneH / 2;
        const cx = LEFT_PAD + pos[i] * trackW;
        const sn = clampN(sp[i] / 0.16, 0, 1);
        // smoothed acceleration → boost (speeding up) / brake (slowing down)
        const rawAccel = (sp[i] - prevVisRef.current[i]) / dts;
        accelSmoothRef.current[i] += (rawAccel - accelSmoothRef.current[i]) * easeA;
        const acc = accelSmoothRef.current[i];
        const boost = clampN(acc * 2.2, 0, 1);
        const brake = clampN(-acc * 2.6, 0, 1);
        // tilt eased toward a small target → no per-tick jerk
        const targetTilt = clampN(-acc * 0.9, -0.16, 0.16);
        tiltRef.current[i] += (targetTilt - tiltRef.current[i]) * easeT;
        const tilt = tiltRef.current[i];

        const arrived = pos[i] >= 0.994;
        // when a rocket VISUALLY crosses the finish, record it so the bet table
        // lights up in sync with the animation (not ~2s ahead on the server clock)
        if (arrived && !arrivedRef.current[i] && data[i] && data[i].finished && data[i].place) {
          arrivedRef.current[i] = true;
          const nm = short(data[i].pair);
          setVisOrder((prev) => (prev.includes(nm) ? prev : [...prev, nm]));
        }
        // ACCELERATION → big flame burst; braking handled below with shock waves
        const flameLen = arrived ? 0.06 : sn * 0.55 + boost * 2.3;
        spawnExhaust(particlesRef.current, cx - shipSize * 0.4, cy, arrived ? 0 : sn * 0.6 + boost * 1.8, SHIP_COLORS[i]);
        drawFlame(ctx, cx, cy, shipSize, flameLen, SHIP_COLORS[i], ts + i * 99);
        if (!arrived && boost > 0.22) {
          drawBoostFlare(ctx, cx - shipSize * 0.5, cy, shipSize, boost, ts);
        }
        // BRAKING → "sound barrier" compression shock ahead of the ship
        if (!arrived && brake > 0.35 && sn > 0.15) {
          drawShock(ctx, cx + shipSize * 0.75, cy, shipSize, brake, ts);
        }
        drawCraft(ctx, cx, cy, shipSize, SHIP_COLORS[i], arrived ? 0 : tilt, i === leader && avgSp > 0.02);
        if (arrived && data[i] && data[i].finished && data[i].place) {
          drawBadge(ctx, cx, cy - shipSize - 6, data[i].place, shipSize * 0.5);
        }
      }
      drawParticles(ctx, particlesRef.current, dt);
      ctx.restore();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    window.addEventListener("resize", resizeCanvas);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resizeCanvas);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const refreshMe = () => {
    if (!localStorage.getItem("token")) return;
    fetch(RACE_BASE + "/me?currency=" + encodeURIComponent(currency), {
      headers: authHeaders(),
    })
      .then((r) => r.json())
      .then((r) => {
        if (!r || r.error) return;
        setBalance(r.balance);
        setMyBets(r.bets || []);
      })
      .catch(() => {});
  };

  useEffect(refreshMe, [currency]);

  // attach per-ship acceleration (Δ speed vs previous tick) for the data table
  const decorate = (arr) => {
    const prev = prevSpeedRef.current;
    arr.forEach((s, i) => {
      s.accel = (s.speed || 0) - (prev[i] === undefined ? s.speed || 0 : prev[i]);
    });
    prevSpeedRef.current = arr.map((s) => s.speed || 0);
    return arr;
  };

  useEffect(() => {
    let ws;
    let closed = false;
    const connect = () => {
      ws = new WebSocket(wsUrl());
      ws.onopen = () => {
        setConn("online");
        setAttempt(0);
        refreshMe();
      };
      ws.onmessage = (e) => {
        const m = JSON.parse(e.data);
        if (m.type === "snapshot" || m.type === "phase") {
          const newRound = m.round_id !== roundRef.current;
          setPhase(m.phase);
          endsAtRef.current = m.ends_at || 0;
          roundRef.current = m.round_id;
          setRoundId(m.round_id);
          if (m.pairs) setPairs(m.pairs);
          if (m.phase === "betting") {
            bettingOpenRef.current = true;
            resetBuffers([0, 0, 0, 0]); // ships snap back to start
            particlesRef.current = [];
            if (newRound) setRoundBets([]); // fresh table for the new round
            prevSpeedRef.current = [];
            lastNRef.current = 0;
            setDirs([[], [], [], []]);
            setSeries([[], [], [], []]);
            setVisOrder([]);
            arrivedRef.current = [false, false, false, false];
            const ph = (m.pairs || []).map((p) => ({
              pair: p, delta_pct: 0, cum_pct: 0, speed: 0, accel: 0,
              p_live: 0.25, finished: false, place: 0,
            }));
            shipsDataRef.current = ph;
            setShips([]);
            setMarkets(m.markets || null);
          } else {
            bettingOpenRef.current = false;
            const positions = m.ships ? m.ships.map((s) => s.pos || 0) : [0, 0, 0, 0];
            if (newRound) {
              // joining mid-race → seed the buffer AT current positions (no catch-up dash)
              resetBuffers(positions);
              arrivedRef.current = [0, 1, 2, 3].map(
                (i) => !!(m.ships && m.ships[i] && m.ships[i].finished)
              );
              setVisOrder(
                (m.ships || [])
                  .filter((s) => s.finished && s.place)
                  .sort((a, b) => a.place - b.place)
                  .map((s) => short(s.pair))
              );
              // rebuild the 20-tick arrow history from the snapshot markers
              if (m.ticks && m.ticks.length) {
                const nd = [[], [], [], []];
                m.ticks.forEach((tk) =>
                  tk.forEach((s, i) => {
                    const d = s.nd || 0; // applied sign (accel/brake), matches the race
                    nd[i].push(d > 0 ? 1 : d < 0 ? -1 : 0);
                  })
                );
                setDirs(nd.map((a) => a.slice(-20)));
                const sr = [[], [], [], []];
                m.ticks.forEach((tk) =>
                  tk.forEach((s, i) => sr[i].push(s.cum_pct || 0))
                );
                setSeries(sr.map((a) => a.slice(-40)));
                const last = m.ticks[m.ticks.length - 1];
                prevSpeedRef.current = last.map((s) => s.speed || 0);
              }
            }
            if (m.ships) {
              pushBatch(positions);
              decorate(m.ships);
              shipsDataRef.current = m.ships;
              setShips(m.ships);
            }
            lastNRef.current = m.n || 0;
            setMarkets(null);
          }
          if (m.history) setHistory(m.history);
        } else if (m.type === "bets_feed") {
          setFeed(m.bets || []);
          setRoundBets((m.bets || []).filter((b) => b.round_id === roundRef.current));
        } else if (m.type === "bet") {
          setFeed((f) => [m, ...f].slice(0, 25));
          if (m.round_id === roundRef.current) {
            setRoundBets((r) => (r.some((x) => x.id === m.id) ? r : [m, ...r]));
          }
        } else if (m.type === "bet_cancel") {
          setFeed((f) => f.filter((x) => x.id !== m.id));
          setRoundBets((r) => r.filter((x) => x.id !== m.id));
        } else if (m.type === "tick") {
          if ((m.n || 0) <= lastNRef.current) return; // dedup / already applied via snapshot
          lastNRef.current = m.n || 0;
          pushBatch(m.ships.map((s) => s.pos || 0));
          decorate(m.ships);
          shipsDataRef.current = m.ships;
          setShips(m.ships);
          setDirs((prev) =>
            m.ships.map((s, i) => {
              const d = s.nd || 0; // applied sign = did the ship actually speed up / slow down
              const sign = d > 0 ? 1 : d < 0 ? -1 : 0;
              return [...(prev[i] || []), sign].slice(-20);
            })
          );
          setSeries((prev) =>
            m.ships.map((s, i) => [...(prev[i] || []), s.cum_pct || 0].slice(-40))
          );
        } else if (m.type === "result") {
          bettingOpenRef.current = false;
          setPhase("settle");
          endsAtRef.current = m.ends_at || 0;
          pushBatch(m.ships.map((s) => s.pos || 0)); // glide to finish
          decorate(m.ships);
          shipsDataRef.current = m.ships;
          setShips(m.ships);
          setHistory((h) => [
            { round_id: m.round_id, placements: m.placements },
            ...h,
          ].slice(0, 10));
          refreshMe();
        }
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch (e) {}
      };
      ws.onclose = () => {
        if (!closed) {
          setConn("offline");
          setAttempt((a) => a + 1);
          setTimeout(connect, 2000);
        }
      };
    };
    connect();
    return () => {
      closed = true;
      if (ws) ws.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const arity = modeArity(mode);
  const complete = picks.length === arity;

  // odds for the currently staged selection (only when the round is taking bets)
  let market = null;
  if (complete && markets && markets[mode]) {
    market = markets[mode].find((m) => m.sel.join() === picks.join()) || null;
  }
  const potential = market ? (parseFloat(amount) || 0) * market.x : 0;

  const chooseMode = (key) => {
    setMode(key);
    // winner mode auto-selects the first card; combos start empty
    setPicks(key === "winner" ? [0] : []);
    setMsg("");
  };

  // cyclic fill: assign positions in order; when the slip is full the next click
  // starts a fresh selection at 1st place; a card can't hold two positions
  const toggleSlot = (i) => {
    setMsg("");
    setPicks((prev) => {
      if (prev.length >= arity) return [i]; // full → restart from 1st place
      if (prev.includes(i)) return prev.filter((x) => x !== i); // unpick to fix a mistake
      return [...prev, i]; // next position
    });
  };
  const clearPicks = () => {
    setPicks([]);
    setMsg("");
  };

  // one bet per round: the user's open bet in the current round (from /me)
  const myRoundBet = myBets.find(
    (b) => b.round_id === roundId && b.status === "open"
  );
  const hasBet = !!myRoundBet;

  const canBet =
    !hasBet &&
    complete &&
    !!market &&
    (parseFloat(amount) || 0) > 0 &&
    phase === "betting" &&
    bettingOpenRef.current;

  const cancelBet = () => {
    fetch(RACE_BASE + "/cancel", {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ round_id: roundRef.current }),
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.ok) {
          setMsg("Ставка отменена");
          refreshMe();
        } else {
          setMsg("✕ " + r.error);
        }
      })
      .catch(() => setMsg("✕ Ошибка сети"));
  };

  const placeBet = () => {
    if (!canBet) return;
    fetch(RACE_BASE + "/bets", {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        round_id: roundRef.current,
        kind: mode,
        selection: picks.map((i) => pairs[i]),
        currency: currency.toUpperCase(),
        amount: parseFloat(amount),
      }),
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.ok) {
          setMsg(
            "✓ Ставка принята · x" + r.bet.x + " · выигрыш до " +
            (parseFloat(r.bet.amount) * r.bet.x).toFixed(4) + " " + r.bet.currency
          );
          setBalance(r.balance);
          refreshMe();
          // slip stays as-is — bet again next round without re-picking
        } else {
          setMsg("✕ " + r.error);
        }
      })
      .catch(() => setMsg("✕ Ошибка сети"));
  };

  if (!isAllowed()) {
    return (
      <StyledContainer>
        <Helmet><title>Crypto Race</title></Helmet>
        <Wrap>
          <div className="closed" style={{ padding: "40px 16px" }}>
            This game is not available yet.
          </div>
        </Wrap>
      </StyledContainer>
    );
  }

  if (tooSmall) {
    return (
      <StyledContainer>
        <Helmet><title>Crypto Race</title></Helmet>
        <Wrap>
          <div className="smallnote">
            🚀 Crypto Race лучше открывать на планшете или компьютере.<br />
            Для этой игры нужен экран пошире — разверните устройство горизонтально
            или зайдите с большего экрана.
          </div>
        </Wrap>
      </StyledContainer>
    );
  }

  // solo win coefficient per slot, shown as a hint on cards
  const soloX = (i) =>
    markets && markets.winner
      ? (markets.winner.find((m) => m.sel[0] === i) || {}).x
      : null;

  const boardShips = ships.length
    ? ships
    : pairs.map((p) => ({ pair: p, pos: 0, delta_pct: 0 }));

  // finishing order by VISUAL arrival → bet rows resolve in sync with the rockets
  const finOrder = visOrder;
  const resolveBet = (b) => {
    const sel = b.selection || [];
    for (let j = 0; j < sel.length; j++) {
      if (finOrder[j] !== undefined && finOrder[j] !== sel[j]) return "lost";
    }
    if (sel.length && sel.every((v, j) => finOrder[j] === v)) return "won";
    return "open";
  };
  const myBetId = myBets.find(
    (b) => b.round_id === roundId && b.status === "open"
  )?.id;

  // winners rise to the top, losers sink, in-progress stay in the middle
  const RANK = { won: 0, open: 1, lost: 2 };
  const rankedBets = roundBets
    .map((b) => ({ b, st: resolveBet(b) }))
    .sort((a, c) => RANK[a.st] - RANK[c.st]);

  // normalized (-1..1) overlay chart of the 4 pairs' cumulative % move
  const seriesLen = Math.max(1, ...series.map((s) => s.length));
  const seriesMax = Math.max(1e-6, ...series.flatMap((s) => s.map(Math.abs)));
  const chartPoints = (arr) =>
    arr
      .map((v, k) => {
        const x = seriesLen > 1 ? (k / (seriesLen - 1)) * 100 : 0;
        const y = 30 - (v / seriesMax) * 27; // [-1,1] → [3,57], zero at 30
        return x.toFixed(2) + "," + y.toFixed(2);
      })
      .join(" ");

  return (
    <StyledContainer>
      <Helmet><title>Crypto Race</title></Helmet>
      <Wrap>
        <div className="board">
          <PhaseTimer
            phase={phase}
            roundId={roundId}
            conn={conn}
            attempt={attempt}
            endsAtRef={endsAtRef}
          />
          <div className="scene-wrap">
            <canvas ref={canvasRef} className="scene" />
          </div>

          {/* detailed per-ship telemetry: last 20 tick directions as arrows */}
          <table className="statstable">
            <thead>
              <tr>
                <th>Ракета</th>
                <th>Курс Δ</th>
                <th className="trend-h" colSpan={20}>Последние 20 тиков</th>
                <th>Шанс / Место</th>
              </tr>
            </thead>
            <tbody>
              {boardShips.map((s, i) => {
                const hist = dirs[i] || [];
                const pad = Array(Math.max(0, 20 - hist.length)).fill(null);
                const cells = [...pad, ...hist];
                return (
                  <tr key={i}>
                    <td>
                      <span className="sdot" style={{ background: SHIP_COLORS[i] }} />
                      <b style={{ color: SHIP_COLORS[i] }}>{short(s.pair)}</b>
                    </td>
                    <td className={(s.delta_pct || 0) >= 0 ? "up" : "down"}>
                      {(s.delta_pct || 0) >= 0 ? "+" : ""}
                      {(s.delta_pct || 0).toFixed(3)}%
                    </td>
                    {cells.map((c, k) => (
                      <td key={k} className="arrow">
                        {c === 1 ? (
                          <span className="up">▲</span>
                        ) : c === -1 ? (
                          <span className="down">▼</span>
                        ) : (
                          <span className="flat">·</span>
                        )}
                      </td>
                    ))}
                    <td>
                      {s.finished && s.place ? (
                        <span className="pl">{s.place}-е</span>
                      ) : s.p_live ? (
                        <span className="live">{(s.p_live * 100).toFixed(0)}%</span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* who is betting on the current round */}
          <div className="roundbets">
            <div className="rb-title">СТАВКИ НА ЭТУ ИГРУ · {roundBets.length}</div>
            {roundBets.length === 0 ? (
              <div className="closed">Пока никто не поставил</div>
            ) : (
              rankedBets.map(({ b, st }) => {
                const mine = b.id === myBetId;
                return (
                  <div className={"rb-row " + st + (mine ? " mine" : "")} key={b.id}>
                    <span className="rb-who">{mine ? "Вы" : b.who}</span>
                    <span className="rb-sel">
                      {KIND_LABEL[b.kind] || b.kind} {b.selection.join("→")}
                    </span>
                    <span className="rb-amt">
                      {parseFloat(b.amount)} {b.currency}
                    </span>
                    <span className="rb-x">x{b.x}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="side">
          <div className="panel wallet">
            <div className="row">
              <span>Баланс</span>
              <b>
                {balance !== null ? parseFloat(balance).toFixed(4) : "—"}{" "}
                <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  <option>USDT</option>
                  <option>USDC</option>
                  <option>BTC</option>
                  <option>ETH</option>
                  <option>SOL</option>
                  <option>TRX</option>
                  <option>7XBT</option>
                </select>
              </b>
            </div>
          </div>

          <div className="panel slip">
            <div className="ptitle">
              СТАВКА
              {markets && markets.warmup ? <span className="warm"> warmup odds</span> : null}
            </div>

            <div className={"slipbody" + (hasBet ? " locked" : "")}>
            {/* mode selector — static */}
            <div className="modes">
              {MODES.map((mm) => (
                <button
                  key={mm.key}
                  className={"mode" + (mode === mm.key ? " on" : "")}
                  onClick={() => chooseMode(mm.key)}
                >
                  {mm.label}
                </button>
              ))}
            </div>
            <div className="mrow">
              <span className="mhint">{MODES.find((x) => x.key === mode).hint}</span>
              {picks.length ? (
                <button className="clear" onClick={clearPicks}>очистить</button>
              ) : null}
            </div>

            {/* static 4-player grid; click to assign order (cyclic) */}
            <div className="grid">
              {[0, 1, 2, 3].map((i) => {
                const pos = picks.indexOf(i);
                const name = short((boardShips[i] || {}).pair) || "—";
                const sx = soloX(i);
                return (
                  <div
                    key={i}
                    className={"card" + (pos !== -1 ? " on" : "")}
                    style={pos !== -1 ? { borderColor: SHIP_COLORS[i] } : null}
                    onClick={() => toggleSlot(i)}
                  >
                    <span className="dot" style={{ background: SHIP_COLORS[i] }} />
                    <span className="cname">{name}</span>
                    {sx ? <span className="cx">x{sx.toFixed(2)}</span> : null}
                    {pos !== -1 ? <span className="badge">{pos + 1}</span> : null}
                  </div>
                );
              })}
            </div>

            {/* ordered selection summary */}
            {picks.length ? (
              <div className="pickrow">
                {picks.map((i, k) => (
                  <span className="pick" key={i}>
                    <b>{ORD[k] || k + 1}</b> {short((boardShips[i] || {}).pair)}
                  </span>
                ))}
                {picks.length < arity ? (
                  <span className="pickmore">
                    · ещё {arity - picks.length}
                  </span>
                ) : null}
              </div>
            ) : null}

            {/* amount + odds + potential payout */}
            <div className="calc">
              <div className="frow">
                <label>Сумма</label>
                <input
                  type="number"
                  value={amount}
                  min="0"
                  step="0.1"
                  onChange={(e) => setAmount(e.target.value)}
                />
                <span className="cur">{currency}</span>
              </div>
              <div className="frow info">
                <span>Коэффициент</span>
                <b className="kx">
                  {complete
                    ? market
                      ? "x" + market.x.toFixed(2)
                      : "—"
                    : "выберите " + arity + " игрок(а)"}
                </b>
              </div>
              <div className="frow info">
                <span>Возможный выигрыш</span>
                <b className="kwin">
                  {market ? potential.toFixed(4) + " " + currency : "—"}
                </b>
              </div>
            </div>
            </div>

            {hasBet ? (
              <div className="placed">
                <div className="placed-info">
                  Ставка принята: {KIND_LABEL[myRoundBet.kind] || myRoundBet.kind}{" "}
                  {myRoundBet.selection.map(short).join("→")} ·{" "}
                  {parseFloat(myRoundBet.amount)} {myRoundBet.currency} · x{myRoundBet.x}
                </div>
                {phase === "betting" && bettingOpenRef.current ? (
                  <button className="cancelbet" onClick={cancelBet}>
                    ОТМЕНИТЬ СТАВКУ
                  </button>
                ) : (
                  <button className="makebet" disabled>
                    СТАВКА В ИГРЕ
                  </button>
                )}
              </div>
            ) : (
              <>
                <button className="makebet" disabled={!canBet} onClick={placeBet}>
                  СДЕЛАТЬ СТАВКУ
                </button>
                {!bettingOpenRef.current && phase && phase !== "betting" ? (
                  <div className="closed">Приём ставок закрыт — ждём следующий раунд</div>
                ) : null}
              </>
            )}
            <div className="betmsg">{msg}</div>
          </div>

          <div className="panel">
            <div className="ptitle">МОИ СТАВКИ</div>
            {myBets.length === 0 ? (
              <div className="closed">Ставок пока нет</div>
            ) : (
              myBets.map((b) => (
                <div key={b.id} className={"mybet " + b.status}>
                  #{b.round_id} {b.kind}{" "}
                  {b.selection.map(short).join("→")} {parseFloat(b.amount)} {b.currency}{" "}
                  x{b.x} — {b.status}
                  {b.status === "won" ? " +" + parseFloat(b.payout) : ""}
                </div>
              ))
            )}
          </div>

          <div className="panel">
            <div className="ptitle">ИСТОРИЯ ИГР</div>
            {history.length === 0 ? (
              <div className="closed">—</div>
            ) : (
              <div className="hist">
                {history.map((r) => {
                  const myb = myBets.find((b) => b.round_id === r.round_id);
                  return (
                    <div className="histrow" key={r.round_id}>
                      <span className="hr">#{r.round_id}</span>
                      <span className="ho">
                        {r.placements.map((p, k) => (
                          <span key={k}>
                            {k > 0 ? " → " : ""}
                            <b style={{ color: PLACE_COLOR[k] }}>{short(p)}</b>
                          </span>
                        ))}
                      </span>
                      {myb ? (
                        <span className={"hres " + myb.status}>
                          {myb.status === "won"
                            ? "+" + parseFloat(myb.payout)
                            : myb.status === "lost"
                            ? "−" + parseFloat(myb.amount)
                            : myb.status}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Wrap>
    </StyledContainer>
  );
};

const Wrap = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  padding: 16px;
  color: #dfe3ff;

  .board { flex: 1 1 520px; min-width: 300px; }
  .side { flex: 0 0 340px; }
  @media (max-width: 900px) {
    .board { flex: 1 1 100%; }
    .side { flex: 1 1 100%; }
  }
  .smallnote {
    padding: 48px 20px; text-align: center; line-height: 1.7; font-size: 15px;
    color: #aab2e6;
  }

  .phase { font-size: 18px; margin-bottom: 14px; font-weight: 600; }
  .phase .rid { color: #8891c9; font-weight: 400; font-size: 14px; }
  .phase .conn { float: right; font-size: 12px; font-weight: 500; }
  .phase .conn.online { color: #6ee7a0; }
  .phase .conn.connecting { color: #f0c674; }
  .phase .conn.offline { color: #ff8093; animation: blink 1s steps(2) infinite; }
  @keyframes blink { 50% { opacity: 0.4; } }

  .scene-wrap {
    position: relative; border-radius: 12px; overflow: hidden;
    box-shadow: 0 8px 30px rgba(0,0,0,0.4), inset 0 0 0 1px #1c2450;
  }
  .scene { display: block; width: 100%; }
  .up { color: #6ee7a0; } .down { color: #ff8093; } .live { color: #f0c674; }

  .tel-toggle { display: flex; gap: 6px; margin-top: 12px; }
  .tel-toggle button {
    padding: 6px 12px; font-size: 12px; cursor: pointer; border-radius: 8px;
    background: #161d38; color: #9aa3dd; border: 1px solid #2a3358;
  }
  .tel-toggle button.on { background: #2a356b; color: #fff; border-color: #5b8def; }
  .chart {
    margin-top: 8px; background: #11162b; border-radius: 10px; padding: 10px;
  }
  .chartsvg { width: 100%; height: 180px; display: block; }
  .chartsvg .zero { stroke: #2a3358; stroke-width: 0.3; stroke-dasharray: 1 1; }
  .chart-legend {
    display: flex; gap: 12px; flex-wrap: wrap; font-size: 11px; margin-top: 6px;
  }
  .chart-legend .chart-scale { margin-left: auto; color: #6f79ad; }
  .statstable {
    width: 100%; margin-top: 12px; border-collapse: collapse; font-size: 12px;
    background: #11162b; border-radius: 10px; overflow: hidden;
  }
  .statstable th {
    text-align: left; font-weight: 500; color: #6f79ad; font-size: 11px;
    padding: 8px 10px; border-bottom: 1px solid #1c2450; letter-spacing: 0.5px;
  }
  .statstable td { padding: 7px 6px; border-bottom: 1px solid #141a33; }
  .statstable tr:last-child td { border-bottom: none; }
  .statstable td:first-child, .statstable th:first-child { padding-left: 10px; }
  .sdot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; margin-right: 7px; vertical-align: middle; }
  .trend-h { text-align: center !important; }
  .statstable td.arrow { text-align: center; padding: 7px 0; font-size: 9px; line-height: 1; }
  td.arrow .up { color: #6ee7a0; } td.arrow .down { color: #ff8093; } td.arrow .flat { color: #3d4a6b; }
  .statstable .pl { color: #f0c674; font-weight: 700; }
  @media (max-width: 620px) { .statstable { font-size: 10px; } .statstable td.arrow { font-size: 8px; } }

  .roundbets { margin-top: 16px; background: #11162b; border-radius: 10px; padding: 12px; }
  .rb-title { font-size: 12px; color: #9aa3dd; letter-spacing: 1px; margin-bottom: 8px; }
  .rb-row {
    display: flex; align-items: center; gap: 8px; font-size: 12px; padding: 6px 8px;
    border-radius: 6px; margin: 3px 0; color: #aab2e6;
    border: 1px solid transparent; transition: background .2s, border-color .2s;
  }
  .rb-row.won { background: rgba(78,175,125,0.15); color: #8ff0be; }
  .rb-row.lost { background: rgba(224,92,110,0.13); color: #ff9aa8; }
  .rb-row.mine { border-color: #5b8def; box-shadow: 0 0 0 1px #5b8def inset; }
  .rb-who { width: 70px; flex: 0 0 auto; font-weight: 600; }
  .rb-sel { flex: 1; }
  .rb-amt { color: #c3c9f5; }
  .rb-x { color: #f0c674; width: 46px; text-align: right; }

  .panel { background: #11162b; border-radius: 10px; padding: 14px; margin-bottom: 14px; }
  .ptitle { font-size: 13px; color: #9aa3dd; margin-bottom: 10px; letter-spacing: 1px; }
  .warm { color: #f0c674; letter-spacing: 0; }
  .wallet .row { display: flex; justify-content: space-between; align-items: center; font-size: 14px; }

  /* mode selector */
  .modes { display: flex; gap: 6px; margin-bottom: 6px; }
  .mode {
    flex: 1; padding: 8px 4px; font-size: 12px; cursor: pointer;
    background: #161d38; color: #9aa3dd; border: 1px solid #2a3358; border-radius: 8px;
    transition: all .15s;
  }
  .mode:hover { background: #1c2445; }
  .mode.on { background: #2a356b; color: #fff; border-color: #5b8def; }
  .mrow { display: flex; justify-content: space-between; align-items: center; margin: 4px 0 12px; }
  .mhint { font-size: 11px; color: #6f79ad; }
  .clear {
    font-size: 11px; padding: 3px 8px; background: transparent; color: #8891c9;
    border: 1px solid #2a3358; border-radius: 6px; cursor: pointer;
  }
  .clear:hover { background: #1c2445; color: #dfe3ff; }
  .pickrow {
    display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
    margin-top: 10px; font-size: 12px; color: #c3c9f5;
  }
  .pick { background: #1a2350; padding: 3px 8px; border-radius: 6px; }
  .pick b { color: #5b8def; }
  .pickmore { color: #6f79ad; }

  /* static player grid */
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .card {
    position: relative; display: flex; align-items: center; gap: 8px;
    padding: 12px 10px; border-radius: 10px; cursor: pointer;
    background: #161d38; border: 2px solid #232b4d; transition: all .12s; user-select: none;
  }
  .card:hover { background: #1c2445; }
  .card.on { background: #1a2350; }
  .dot { width: 10px; height: 10px; border-radius: 50%; flex: 0 0 auto; }
  .cname { font-size: 14px; font-weight: 600; flex: 1; }
  .cx { font-size: 12px; color: #f0c674; }
  .badge {
    position: absolute; top: -8px; right: -8px; width: 22px; height: 22px;
    border-radius: 50%; background: #5b8def; color: #fff; font-size: 12px; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 0 0 2px #11162b;
  }

  /* amount + odds */
  .calc { margin-top: 14px; }
  .frow { display: flex; align-items: center; gap: 8px; margin: 8px 0; }
  .frow label { width: 90px; font-size: 13px; color: #9aa3dd; }
  .frow input { flex: 1; }
  .frow .cur { font-size: 12px; color: #8891c9; }
  .frow.info { justify-content: space-between; font-size: 13px; color: #9aa3dd; }
  .kx { color: #f0c674; }
  .kwin { color: #4caf7d; }

  input, select, button {
    background: #1c2445; color: #dfe3ff; border: 1px solid #3d4a8f;
    border-radius: 6px; padding: 8px 10px; font-size: 14px;
  }
  .slipbody.locked { opacity: 0.4; pointer-events: none; filter: grayscale(0.3); }
  .makebet {
    width: 100%; margin-top: 12px; padding: 12px; font-weight: 700; letter-spacing: 1px;
    background: linear-gradient(120deg, #2f6fed, #6a3ff0); border: none; cursor: pointer;
    color: #fff; border-radius: 6px;
  }
  .makebet:hover { filter: brightness(1.1); }
  .makebet:disabled { background: #2a3358; color: #6f79ad; cursor: not-allowed; filter: none; }
  .placed { margin-top: 12px; }
  .placed-info {
    font-size: 12px; color: #6ee7a0; background: #12241c; border: 1px solid #1e5b3a;
    border-radius: 8px; padding: 10px; margin-bottom: 10px; text-align: center;
  }
  .cancelbet {
    width: 100%; padding: 12px; font-weight: 700; letter-spacing: 1px; color: #fff;
    background: linear-gradient(120deg, #b0413e, #e05c6e); border: none; border-radius: 6px;
    cursor: pointer;
  }
  .cancelbet:hover { filter: brightness(1.1); }

  .betmsg { font-size: 12px; margin-top: 10px; min-height: 16px; color: #c3c9f5; }
  .closed { font-size: 12px; color: #6f79ad; padding: 8px 0; }

  .mybet { font-size: 12px; margin: 4px 0; color: #9aa3dd; }
  .mybet.won { color: #4caf7d; }
  .mybet.lost { color: #e05c6e; }
  .mybet.refunded { color: #f0c674; }

  .hist { max-height: 300px; overflow-y: auto; }
  .histrow {
    display: flex; align-items: center; gap: 8px; font-size: 11px;
    padding: 5px 0; border-bottom: 1px solid #1a2140;
  }
  .histrow .hr { color: #6f79ad; width: 46px; flex: 0 0 auto; }
  .histrow .ho { flex: 1; color: #aab2e6; }
  .histrow .hres { font-weight: 700; }
  .histrow .hres.won { color: #6ee7a0; }
  .histrow .hres.lost { color: #ff8093; }
  .histrow .hres.refunded { color: #f0c674; }

  .feed { max-height: 240px; overflow-y: auto; }
  .feedrow {
    display: flex; align-items: center; gap: 6px; font-size: 11px;
    padding: 4px 0; border-bottom: 1px solid #1a2140; animation: appear .3s ease-out;
  }
  .fwho { color: #9aa3dd; width: 64px; flex: 0 0 auto; }
  .fsel { flex: 1; color: #c3c9f5; }
  .famt { color: #8891c9; }
  .fx { color: #f0c674; width: 46px; text-align: right; }
`;

export default RacePage;
